import {
  AnalyticsActionShareEvent,
  AnalyticsEventParam,
} from '@accedo/one-plugin-sdk';
import { t } from 'i18next';
import { Ref, useImperativeHandle, useState } from 'react';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  TwitterIcon,
  TwitterShareButton,
} from 'react-share';
import styled from 'styled-components';

import { Item } from '#/client';

import { focusShadow } from '../common';
import { Modal } from './Modal';

export interface SharingModalApi {
  open: (item?: Item) => void;
  close: () => void;
}
interface SharingModalProps {
  apiRef?: Ref<SharingModalApi>;
}

// even if we are testing locally, we still have to share the public URL.
// Hide it behind the goal guard to avoid bundling the local url into prod bundle
export const getUrl = () =>
  process.env.goal === 'local'
    ? window.location.href.replace(
        'http://localhost:8080/',
        'https://dev.web.one.accedo.tv/'
      )
    : window.location.href;

const IconRow = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-bottom: 1rem;
  button:focus:not(.focus-visible) {
    outline: none;
  }
  button.focus-visible {
    ${focusShadow}
  }
`;

export const SharingModal = ({ apiRef }: SharingModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [item, setItem] = useState<Item>();

  const api = {
    open: (item?: Item) => {
      setItem(item);
      setIsOpen(true);
    },
    close: () => {
      // #ExternalAnalytics - Share
      // TODO#analytics: only temporary solution to send this here, should be done in a better way
      document.dispatchEvent(
        new AnalyticsActionShareEvent({
          [AnalyticsEventParam.ContentId]: item?.id,
          [AnalyticsEventParam.Genre]: item?.genres,
          [AnalyticsEventParam.Title]: item?.title,
        })
      );

      setIsOpen(false);
    },
  };

  // Unlike the regular Modal, this one uses an imperative handle.
  // That means the PageController don't have to manage the modal open state, so the Page won't rerender every time.
  useImperativeHandle(apiRef, () => api);

  const buttonProps = {
    url: getUrl(),
    onShareWindowClose: api.close,
  };

  const iconProps = {
    round: true,
  };

  return (
    <Modal
      closeOnEsc
      closeOnOverlayClick
      isOpen={isOpen}
      onClose={api.close}
      showCloseButton
    >
      <h2>{t('button.share')}</h2>
      <IconRow>
        <FacebookShareButton {...buttonProps}>
          <FacebookIcon {...iconProps} />
        </FacebookShareButton>
        <TwitterShareButton {...buttonProps}>
          <TwitterIcon {...iconProps} />
        </TwitterShareButton>
        <EmailShareButton {...buttonProps} subject={item?.title}>
          <EmailIcon {...iconProps} />
        </EmailShareButton>
      </IconRow>
    </Modal>
  );
};
